import { Fragment, useRef, useState } from 'react'
import { TIPOS_CAMPO, type CustomField, type CustomFieldPlacement } from './custom-field-data.ts'

/**
 * Editor de layout dos campos de uma aba/barra (SPEC-20260828-1914).
 *
 * Grid de 12 colunas; as LINHAS são derivadas (empacota na ordem até somar 12,
 * até 4 por linha). Ao arrastar um card:
 *  - soltar na LATERAL de outro card → entra na MESMA linha, dividindo as
 *    colunas igualmente (2, 3 ou 4 por linha);
 *  - soltar no MEIO (topo/baixo) → vira uma NOVA linha (marcador horizontal).
 * A alça da direita ajusta a largura (proporção da linha). O item de trabalho
 * renderiza o mesmo grid.
 */

const MIME = 'application/x-orbind-placement'
const MIN = 3 // largura mínima → até 4 por linha (12/4)
const MAX_COLS = 4
const rotulo = (labels: Record<string, string>, fallback: string) =>
  labels['pt-BR'] || Object.values(labels)[0] || fallback

type Item = { placement: CustomFieldPlacement; campo: CustomField }
type Modo = 'lado-antes' | 'lado-depois' | 'linha-antes' | 'linha-depois'

/** A colocação tem alguma condição configurada? (destaca a engrenagem) */
const hasRegra = (p: CustomFieldPlacement) => Boolean(p.config?.visibilityRule || p.config?.requiredRule)

/** Larguras iguais para uma linha de n cards, somando 12 (2→6/6, 3→4/4/4, 4→3/3/3/3). */
const dividir = (n: number, i: number) => {
  const base = Math.floor(12 / n)
  const rem = 12 - base * n
  return base + (i < rem ? 1 : 0)
}

export function AbaLayoutEditor({
  itens,
  onSalvarLayout,
  onEditar,
  onRemover,
  onConfig,
}: {
  itens: Item[]
  onSalvarLayout: (items: { id: string; sortOrder: number; width: number }[]) => void
  onEditar: (campo: CustomField) => void
  onRemover: (placementId: string) => void
  onConfig: (placement: CustomFieldPlacement, campo: CustomField) => void
}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const marcaRef = useRef<HTMLSpanElement>(null)
  /** Régua do redimensionamento: onde a borda direita do campo vai parar. */
  const [redim, setRedim] = useState<{ id: string; cols: number; x: number; y: number; alt: number } | null>(null)
  const resize = useRef<{
    id: string
    x: number
    larg: number
    passo: number
    colPx: number
    gap: number
    esq: number
    topo: number
    alt: number
    atual: number
  } | null>(null)

  const larguraDe = (p: CustomFieldPlacement) => Math.min(12, Math.max(1, p.width || 12))

  /** Linhas atuais (ids), empacotando por largura na ordem (máx. 4 por linha). */
  const linhasIds = () => {
    const rows: string[][] = []
    let cur: string[] = []
    let sum = 0
    for (const it of itens) {
      const w = larguraDe(it.placement)
      if (cur.length && (sum + w > 12 || cur.length >= MAX_COLS)) {
        rows.push(cur)
        cur = []
        sum = 0
      }
      cur.push(it.placement.id)
      sum += w
    }
    if (cur.length) rows.push(cur)
    return rows
  }

  /**
   * Reescala as larguras de uma linha para somar exatamente 12, mantendo a
   * proporcao. Sem isso uma linha "sobrando" espaco puxava o primeiro campo da
   * linha seguinte para dentro dela no empacotamento.
   */
  const completar = (ws: number[]) => {
    const soma = ws.reduce((a, b) => a + b, 0)
    if (soma === 12) return ws
    const out = ws.map((w) => Math.max(MIN, Math.round((w * 12) / soma)))
    const dif = 12 - out.reduce((a, b) => a + b, 0)
    out[out.length - 1] = Math.max(MIN, out[out.length - 1]! + dif)
    return out
  }

  /** Grava ordem + largura a partir das linhas resultantes. */
  const gravar = (rows: string[][], chavesOld: string[]) => {
    const items: { id: string; sortOrder: number; width: number }[] = []
    let ordem = 0
    for (const r of rows) {
      if (r.length === 1) {
        items.push({ id: r[0]!, sortOrder: ordem++, width: 12 })
        continue
      }
      const larg = chavesOld.includes(r.join(','))
        ? completar(r.map((id) => larguraDe(itens.find((x) => x.placement.id === id)!.placement)))
        : r.map((_, i) => dividir(r.length, i))
      r.forEach((id, i) => items.push({ id, sortOrder: ordem++, width: larg[i]! }))
    }
    onSalvarLayout(items)
  }

  /** Solta na area vazia do grid: vira a ultima linha, sozinho. */
  const soltarNoFim = (draggedId: string) => {
    const rowsOld = linhasIds()
    if (rowsOld[rowsOld.length - 1]?.join(',') === draggedId) return
    const rows = rowsOld.map((r) => r.filter((id) => id !== draggedId)).filter((r) => r.length > 0)
    rows.push([draggedId])
    gravar(rows, rowsOld.map((r) => r.join(',')))
  }

  const soltar = (draggedId: string, targetId: string, modo: Modo) => {
    if (draggedId === targetId) return
    const rowsOld = linhasIds()
    const chavesOld = rowsOld.map((r) => r.join(','))
    // tira o arrastado de onde estava
    let rows = rowsOld.map((r) => r.filter((id) => id !== draggedId)).filter((r) => r.length > 0)
    const ri = rows.findIndex((r) => r.includes(targetId))
    if (ri < 0) return
    if (modo === 'lado-antes' || modo === 'lado-depois') {
      const row = rows[ri]!
      if (row.length >= MAX_COLS) {
        // linha cheia: entra como linha própria ao lado
        rows.splice(modo === 'lado-depois' ? ri + 1 : ri, 0, [draggedId])
      } else {
        const ti = row.indexOf(targetId)
        row.splice(modo === 'lado-depois' ? ti + 1 : ti, 0, draggedId)
      }
    } else {
      rows.splice(modo === 'linha-antes' ? ri : ri + 1, 0, [draggedId])
    }
    // linha com 1 card ocupa a linha inteira; linhas cuja composição não mudou
    // mantêm as larguras (reescaladas para 12); as que mudaram dividem igual.
    gravar(rows, chavesOld)
  }

  const modoNoPonto = (card: HTMLElement, x: number, y: number): Modo => {
    const rect = card.getBoundingClientRect()
    const px = (x - rect.left) / rect.width
    if (px < 0.25) return 'lado-antes'
    if (px > 0.75) return 'lado-depois'
    return (y - rect.top) / rect.height < 0.5 ? 'linha-antes' : 'linha-depois'
  }

  /**
   * Redimensionar (revisto 2026-08-31): enquanto arrasta a alça NADA se mexe —
   * antes o layout era reempacotado a cada pixel e os cards pulavam de linha.
   * Agora só uma RÉGUA mostra onde a borda direita vai parar; a largura é
   * aplicada no soltar. A marca de mudar de posição fica escondida.
   */
  const inicioResize = (e: React.PointerEvent, p: CustomFieldPlacement, w: number) => {
    e.stopPropagation()
    e.preventDefault()
    try {
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    } catch {
      /* evento sintético (teste) sem pointer ativo */
    }
    const grid = gridRef.current
    const card = (e.currentTarget as HTMLElement).closest('.abaed__card') as HTMLElement | null
    const gr = grid?.getBoundingClientRect()
    const cr = card?.getBoundingClientRect()
    const gap = grid ? parseFloat(getComputedStyle(grid).columnGap || '0') || 0 : 0
    const gridW = gr?.width ?? 600
    const colPx = (gridW - gap * 11) / 12
    resize.current = {
      id: p.id,
      x: e.clientX,
      larg: w,
      colPx,
      gap,
      passo: colPx + gap,
      esq: (cr?.left ?? 0) - (gr?.left ?? 0),
      topo: (cr?.top ?? 0) - (gr?.top ?? 0),
      alt: cr?.height ?? 0,
      atual: w,
    }
    limparMarca()
    marcaDe(w)
  }

  /** Posiciona a régua na borda direita que a largura `cols` teria. */
  const marcaDe = (cols: number) => {
    const r = resize.current
    if (!r) return
    setRedim({ id: r.id, cols, x: r.esq + cols * r.colPx + (cols - 1) * r.gap, y: r.topo, alt: r.alt })
  }

  const aoRedimensionar = (e: React.PointerEvent) => {
    const r = resize.current
    if (!r) return
    const nova = Math.min(12, Math.max(MIN, r.larg + Math.round((e.clientX - r.x) / r.passo)))
    if (nova === r.atual) return
    r.atual = nova
    marcaDe(nova)
  }

  const fimResize = (e: React.PointerEvent) => {
    const r = resize.current
    if (!r) return
    try {
      ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    } catch {
      /* pointer já solto */
    }
    resize.current = null
    setRedim(null)
    if (r.atual === r.larg) return
    onSalvarLayout(
      itens.map((i, idx) => ({
        id: i.placement.id,
        sortOrder: idx,
        width: i.placement.id === r.id ? r.atual : larguraDe(i.placement),
      })),
    )
  }

  /**
   * Marca de destino (revista 2026-08-31): uma RÉGUA SOLTA desenhada POR CIMA
   * do grid — vertical no vão entre dois campos, ou atravessando a linha
   * inteira quando o campo vai virar uma linha nova.
   *
   * Ela é posicionada direto no DOM, e não por estado: como item do grid ela
   * empurrava as linhas de baixo durante o arrasto, o cartão de origem saía do
   * lugar e o Chrome largava o arrasto em outro alvo — por isso só o último
   * campo da linha conseguia mudar de linha.
   */
  const marcar = (card: HTMLElement, modo: Modo) => {
    const m = marcaRef.current
    const g = gridRef.current
    if (!m || !g) return
    const gr = g.getBoundingClientRect()
    const cr = card.getBoundingClientRect()
    if (modo === 'linha-antes' || modo === 'linha-depois') {
      m.className = 'abaed__marca abaed__marca--linha'
      m.style.left = '0px'
      m.style.width = '100%'
      m.style.height = '3px'
      m.style.top = `${(modo === 'linha-antes' ? cr.top - 5 : cr.bottom + 2) - gr.top}px`
    } else {
      m.className = 'abaed__marca abaed__marca--lado'
      m.style.width = '3px'
      m.style.left = `${(modo === 'lado-antes' ? cr.left - 5 : cr.right + 2) - gr.left}px`
      m.style.top = `${cr.top - gr.top}px`
      m.style.height = `${cr.height}px`
    }
  }
  const limparMarca = () => {
    if (marcaRef.current) marcaRef.current.className = 'abaed__marca'
  }

  const cartao = ({ placement: p, campo: c }: Item) => {
        const w = larguraDe(p)
        return (
          <div
            key={p.id}
            className="abaed__card"
            style={{ gridColumn: `span ${w}` }}
            draggable={!redim}
            onDragStart={(e) => {
              e.dataTransfer.setData(MIME, p.id)
              e.dataTransfer.effectAllowed = 'move'
              e.currentTarget.classList.add('abaed__card--arrastando')
            }}
            onDragEnd={(e) => {
              e.currentTarget.classList.remove('abaed__card--arrastando')
              limparMarca()
            }}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(MIME)) return
              e.preventDefault()
              e.stopPropagation()
              marcar(e.currentTarget, modoNoPonto(e.currentTarget, e.clientX, e.clientY))
            }}
            onDrop={(e) => {
              const dragged = e.dataTransfer.getData(MIME)
              if (!dragged) return
              e.preventDefault()
              e.stopPropagation()
              limparMarca()
              soltar(dragged, p.id, modoNoPonto(e.currentTarget, e.clientX, e.clientY))
            }}
          >
            {/* UMA linha só (pedido 2026-08-30): nome, tipo e largura ficam
                lado a lado com as ações — nada quebra para a linha de baixo. */}
            <div className="abaed__topo">
              <i className="fas fa-grip-vertical abaed__grip" aria-hidden title="Arraste para mover" />
              <button type="button" className="abaed__nome" onClick={() => onEditar(c)} title="Editar campo">
                {rotulo(c.labels, c.key)}
                {c.required ? <span className="ccf__obrig"> *</span> : null}
              </button>
              <span className="abaed__tipo">{TIPOS_CAMPO.find((x) => x.value === c.type)?.label ?? c.type}</span>
              <span className="abaed__larg">{w === 12 ? 'linha inteira' : `${w}/12`}</span>
              <button
                type="button"
                className={`abaed__cfg${hasRegra(p) ? ' abaed__cfg--on' : ''}`}
                title="Condições (mostrar / exigir quando…)"
                aria-label="Condições do campo"
                onClick={() => onConfig(p, c)}
              >
                <i className="fas fa-cog" aria-hidden />
              </button>
              <button
                type="button"
                className="abaed__x"
                title="Remover desta aba"
                aria-label="Remover desta aba"
                onClick={() => onRemover(p.id)}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
            <span
              className={`abaed__resize${redim?.id === p.id ? ' abaed__resize--on' : ''}`}
              title="Arraste para ajustar a largura"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onPointerDown={(e) => inicioResize(e, p, w)}
              onPointerMove={aoRedimensionar}
              onPointerUp={fimResize}
            />
          </div>
        )
  }

  return (
    <div
      className="abaed"
      ref={gridRef}
      onDragOver={(e) => {
        // área vazia do grid: solta como última linha
        if (e.target !== e.currentTarget || !e.dataTransfer.types.includes(MIME)) return
        e.preventDefault()
        const ultimo = gridRef.current?.querySelector('.abaed__card:last-of-type')
        if (ultimo) marcar(ultimo as HTMLElement, 'linha-depois')
      }}
      onDrop={(e) => {
        if (e.target !== e.currentTarget) return
        const dragged = e.dataTransfer.getData(MIME)
        if (!dragged) return
        e.preventDefault()
        limparMarca()
        soltarNoFim(dragged)
      }}
    >
      {itens.map((it) => (
        <Fragment key={it.placement.id}>{cartao(it)}</Fragment>
      ))}
      <span className="abaed__marca" ref={marcaRef} />
      {redim ? (
        <span className="abaed__medida" style={{ left: redim.x, top: redim.y, height: redim.alt }}>
          <b>{redim.cols === 12 ? 'linha inteira' : `${redim.cols}/12`}</b>
        </span>
      ) : null}
    </div>
  )
}
